import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import { EXAMEN_GRILLE_31, EXAMEN_CATEGORIES, EXAMEN_REGLES, EXAMEN_AVIS } from '../data/examenBlancGrille31';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { QRCodeSVG } from 'qrcode.react';
import BilanRepriseTab from '../components/BilanRepriseTab';
import {
  ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Octagon, ClipboardCheck,
  Trophy, RotateCcw, Printer, ShieldCheck, GraduationCap, QrCode, Send, Copy,
  Loader2, X, Share2, MessageCircle, Mail, Car,
} from 'lucide-react';

const STATES = [
  { v: 'ok',         l: 'OK',          cls: 'bg-emerald-600 text-white border-emerald-500', icon: CheckCircle2 },
  { v: 'ameliorer',  l: 'À améliorer', cls: 'bg-amber-500 text-amber-950 border-amber-400', icon: AlertTriangle },
  { v: 'non_acquis', l: 'Non acquis',  cls: 'bg-red-600 text-white border-red-500',         icon: XCircle },
];

export default function ExamenBlancGrille31Page() {
  const { user } = useAuth();
  const isTeacher = user && user.role === 'enseignant';
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get('tab') === 'permis' ? 'permis' : 'bilan'));
  const [grid, setGrid] = useState({});      // { n: 'ok'|'ameliorer'|'non_acquis' }
  const [elim, setElim] = useState({});      // { n: true } — checkbox éliminatoire par ligne
  const [meta, setMeta] = useState(() => ({ eleve: new URLSearchParams(window.location.search).get('eleve') || '', date: new Date().toISOString().slice(0, 10) }));
  const [resetConfirm, setResetConfirm] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState('idle'); // idle|saving|done|error
  const [shareResult, setShareResult] = useState(null);
  const [shareError, setShareError] = useState('');
  const [copied, setCopied] = useState(false);
  const [conseils, setConseils] = useState('');

  const setOne = (n, v) => setGrid((p) => ({ ...p, [n]: v }));
  const toggleElim = (n) => setElim((p) => ({ ...p, [n]: !p[n] }));

  const stats = useMemo(() => {
    const c = { ok: 0, ameliorer: 0, non_acquis: 0, elim: 0 };
    Object.values(grid).forEach((v) => { if (c[v] !== undefined) c[v]++; });
    Object.values(elim).forEach((b) => { if (b) c.elim++; });
    return c;
  }, [grid, elim]);

  const total = EXAMEN_GRILLE_31.length;
  const evalCount = stats.ok + stats.ameliorer + stats.non_acquis;
  const okPct = total ? Math.round((stats.ok / total) * 100) : 0;
  const hasElim = stats.elim > 0;

  let verdict, vColor, VerdictIcon, verdictKind;
  if (evalCount === 0) {
    verdict = 'Évalue les 31 critères pour obtenir un avis.';
    vColor = 'border-white/10 text-white/60';
    VerdictIcon = ClipboardCheck;
    verdictKind = 'pending';
  } else if (hasElim) {
    verdict = EXAMEN_AVIS.elim + ` (${stats.elim} faute${stats.elim > 1 ? 's' : ''} éliminatoire${stats.elim > 1 ? 's' : ''} cochée${stats.elim > 1 ? 's' : ''})`;
    vColor = 'border-red-700/40 text-red-300 from-red-700/25';
    VerdictIcon = Octagon;
    verdictKind = 'elim';
  } else if (okPct >= EXAMEN_REGLES.ok_ratio_admis) {
    verdict = EXAMEN_AVIS.favorable;
    vColor = 'border-emerald-700/40 text-emerald-300 from-emerald-700/20';
    VerdictIcon = Trophy;
    verdictKind = 'favorable';
  } else if (okPct >= EXAMEN_REGLES.ok_ratio_reserve) {
    verdict = EXAMEN_AVIS.reserve;
    vColor = 'border-sky-700/40 text-sky-300 from-sky-700/20';
    VerdictIcon = AlertTriangle;
    verdictKind = 'reserve';
  } else {
    verdict = EXAMEN_AVIS.ajourne;
    vColor = 'border-amber-700/40 text-amber-300 from-amber-700/20';
    VerdictIcon = XCircle;
    verdictKind = 'ajourne';
  }

  const reset = () => {
    if (!resetConfirm) {
      setResetConfirm(true);
      setTimeout(() => setResetConfirm(false), 3000);
      return;
    }
    setGrid({});
    setElim({});
    setMeta({ eleve: '', date: '' });
    setConseils('');
    setResetConfirm(false);
  };

  const openShare = () => {
    setShareError('');
    setShareResult(null);
    setShareStatus('idle');
    setShareOpen(true);
  };

  const saveAndShare = async () => {
    if (!meta.eleve.trim()) {
      setShareError("Renseigne le nom de l'élève avant de partager.");
      return;
    }
    if (evalCount === 0) {
      setShareError("Évalue au moins un critère avant de partager la fiche.");
      return;
    }
    setShareError('');
    setShareStatus('saving');
    try {
      // Convert date DD/MM/YYYY → ISO if possible
      let isoDate = undefined;
      const m = (meta.date || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (m) isoDate = `${m[3]}-${m[2]}-${m[1]}`;
      else if (/^\d{4}-\d{2}-\d{2}$/.test(meta.date || '')) isoDate = meta.date;
      const payload = {
        student_name: meta.eleve.trim(),
        date: isoDate,
        grid: Object.fromEntries(Object.entries(grid).filter(([, v]) => !!v)),
        elim: Object.fromEntries(Object.entries(elim).filter(([, v]) => !!v)),
        verdict,
        verdict_kind: verdictKind,
        ok_pct: okPct,
        stats: { ok: stats.ok, ameliorer: stats.ameliorer, non_acquis: stats.non_acquis, elim: stats.elim, total },
        conseils: conseils.trim() || null,
      };
      const res = await api.createMockExam(payload);
      const fullUrl = `${window.location.origin}/examen-blanc/${res.token}`;
      setShareResult({ ...res, fullUrl });
      setShareStatus('done');
    } catch (err) {
      setShareStatus('error');
      setShareError(err?.response?.data?.detail || "Impossible d'enregistrer l'examen.");
    }
  };

  const copyUrl = async () => {
    if (!shareResult?.fullUrl) return;
    try {
      await navigator.clipboard.writeText(shareResult.fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* silent */ }
  };

  const buildShareText = () => {
    if (!shareResult) return '';
    return `Bonjour ${meta.eleve},

Voici le récapitulatif de ton examen blanc conduite du ${meta.date ? (meta.date.includes('/') ? meta.date : new Date(`${meta.date}T00:00:00`).toLocaleDateString('fr-FR')) : 'jour'} :
${verdict}
Score : ${okPct}% de critères OK (${stats.ok}/${total}).

Consulte le détail (critères ratés + leçons à revoir) ici :
${shareResult.fullUrl}

À bientôt !`;
  };

  const openSMS = () => {
    const body = encodeURIComponent(buildShareText());
    window.location.href = `sms:?body=${body}`;
  };
  const openWhatsApp = () => {
    const body = encodeURIComponent(buildShareText());
    window.open(`https://wa.me/?text=${body}`, '_blank');
  };
  const openEmail = () => {
    const subj = encodeURIComponent(`Examen blanc conduite · ${meta.date || 'récapitulatif'}`);
    const body = encodeURIComponent(buildShareText());
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
  };

  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 mt-10 mb-16" data-testid="examen-grille-31-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6 no-print">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/cours-interactifs" className="hover:text-white transition">Cours interactifs</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Bilans & permis blanc</span>
        </nav>

        {/* Onglets Bilans / Permis blanc */}
        <div className="flex gap-2 mb-7 flex-wrap">
          <button
            onClick={() => setTab('bilan')}
            data-testid="tab-bilan-reprise"
            className={`h-11 px-5 rounded-full border text-[13px] font-bold inline-flex items-center gap-2 transition ${tab === 'bilan' ? 'bg-emerald-600 border-emerald-500 text-white shadow-lg shadow-emerald-900/40' : 'border-white/15 bg-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
          >
            <Car size={14} /> Bilans de formation
          </button>
          <button
            onClick={() => setTab('permis')}
            data-testid="tab-permis-blanc"
            className={`h-11 px-5 rounded-full border text-[13px] font-bold inline-flex items-center gap-2 transition ${tab === 'permis' ? 'bg-red-600 border-red-500 text-white shadow-lg shadow-red-900/40' : 'border-white/15 bg-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
          >
            <GraduationCap size={14} /> Permis blanc · 31 critères
          </button>
        </div>

        {tab === 'bilan' && <BilanRepriseTab />}
        {tab === 'permis' && (
          <>

        {/* Hero */}
        <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-7 lg:p-9 mb-7 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-red-600/12 blur-3xl" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4">
              <GraduationCap size={13} className="text-red-400" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Évaluation Enseignant ECSR · 31 critères officiels</span>
            </div>
            <h1 className="font-serif-editorial text-white text-[34px] sm:text-[44px] lg:text-[52px] leading-[1.02] tracking-tight">
              Examen blanc<br />
              <span className="text-red-500">grille type permis B</span>
            </h1>
            <p className="text-white/70 text-[15px] mt-5 max-w-2xl leading-relaxed">
              Évalue chacun des <span className="text-red-400 font-semibold">31 critères</span> sur 3 niveaux (OK / À améliorer / Non acquis) et coche les fautes éliminatoires. Le verdict s&apos;affiche en temps réel selon les règles officielles.
            </p>
          </div>
        </div>

        {/* Identité */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 lg:p-5 mb-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec">Élève · Nom / Prénom</span>
              <input
                value={meta.eleve}
                onChange={(e) => setMeta({ ...meta, eleve: e.target.value })}
                data-testid="meta-eleve"
                placeholder="Nom Prénom"
                className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13px] focus:outline-none focus:border-red-500/50"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec">Date d&apos;évaluation · auto (aujourd&apos;hui)</span>
              <input
                type="date"
                value={meta.date}
                onChange={(e) => setMeta({ ...meta, date: e.target.value })}
                data-testid="meta-date"
                style={{ colorScheme: 'dark' }}
                className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13px] focus:outline-none focus:border-red-500/50"
              />
            </label>
          </div>
        </div>

        {/* Stats + verdict */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">Évalué</div>
            <div className="font-serif-editorial text-white text-[26px] leading-none mt-1">{evalCount}<span className="text-white/30 text-[14px]">/{total}</span></div>
          </div>
          <div className="rounded-xl border border-emerald-700/30 bg-emerald-950/15 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-emerald-300 font-bold font-mono-spec">OK</div>
            <div className="font-serif-editorial text-emerald-400 text-[26px] leading-none mt-1">{stats.ok}</div>
          </div>
          <div className="rounded-xl border border-amber-700/30 bg-amber-950/15 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-amber-300 font-bold font-mono-spec">À améliorer</div>
            <div className="font-serif-editorial text-amber-400 text-[26px] leading-none mt-1">{stats.ameliorer}</div>
          </div>
          <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Non acquis</div>
            <div className="font-serif-editorial text-red-400 text-[26px] leading-none mt-1">{stats.non_acquis}</div>
          </div>
          <div className="rounded-xl border-2 border-red-700/50 bg-red-950/30 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Éliminatoires</div>
            <div className="font-serif-editorial text-red-400 text-[26px] leading-none mt-1">{stats.elim}</div>
          </div>
        </div>

        {/* Verdict bar */}
        <div className={`rounded-xl border-2 bg-gradient-to-br to-transparent p-4 mb-6 ${vColor}`} data-testid="examen-verdict">
          <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] font-bold font-mono-spec">
            <VerdictIcon size={12} /> Verdict · {okPct} % OK
          </div>
          <p className="text-white/85 text-[14.5px] mt-2 leading-relaxed font-bold">{verdict}</p>
        </div>

        {/* Grille par catégorie */}
        <div className="space-y-5 mb-8">
          {EXAMEN_CATEGORIES.map((cat) => {
            const rows = EXAMEN_GRILLE_31.filter((r) => r.cat === cat);
            return (
              <div key={cat} className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
                <div className="px-4 py-3 bg-red-600/10 border-b border-red-700/30">
                  <div className="text-[12.5px] text-red-300 font-bold font-mono-spec uppercase tracking-wider">{cat}</div>
                </div>
                <ul className="divide-y divide-white/5">
                  {rows.map((r) => {
                    const cur = grid[r.n];
                    const isElim = !!elim[r.n];
                    return (
                      <li key={r.n} className="grid lg:grid-cols-[50px_1fr_auto] gap-3 px-4 py-3 items-center" data-testid={`row-${r.n}`}>
                        <div className="text-red-400 font-mono-spec text-[12.5px] font-bold">N°{r.n}</div>
                        <div className="text-white text-[13.5px] leading-snug">{r.label}</div>
                        <div className="flex gap-1.5 flex-wrap items-center">
                          {STATES.map((s) => {
                            const I = s.icon;
                            const active = cur === s.v;
                            return (
                              <button
                                key={s.v}
                                onClick={() => setOne(r.n, active ? null : s.v)}
                                data-testid={`row-${r.n}-${s.v}`}
                                className={`h-8 px-2.5 rounded-md border text-[11px] font-bold inline-flex items-center gap-1 transition ${active ? s.cls + ' shadow-md' : 'border-white/10 bg-white/5 text-white/55 hover:bg-white/10 hover:text-white'}`}
                              >
                                <I size={11} /> {s.l}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => toggleElim(r.n)}
                            data-testid={`row-${r.n}-elim`}
                            title="Faute éliminatoire"
                            className={`h-8 px-2.5 rounded-md border text-[11px] font-bold inline-flex items-center gap-1 transition ${isElim ? 'bg-red-700 text-white border-red-600 shadow-md' : 'border-red-700/30 bg-red-950/15 text-red-300 hover:bg-red-900/30'}`}
                          >
                            <Octagon size={11} /> Élim.
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Règle officielle */}
        <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5 mb-6">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-2">Règle de notation</div>
          <ul className="space-y-1.5 text-white/75 text-[13.5px]">
            <li>≥ <span className="text-emerald-400 font-bold">{EXAMEN_REGLES.ok_ratio_admis} %</span> de critères OK et aucune éliminatoire → <span className="text-emerald-400 font-semibold">Avis favorable</span>.</li>
            <li>De {EXAMEN_REGLES.ok_ratio_reserve} % à {EXAMEN_REGLES.ok_ratio_admis - 1} % OK → <span className="text-sky-400 font-semibold">Avis réservé</span> (à retravailler).</li>
            <li>&lt; {EXAMEN_REGLES.ok_ratio_reserve} % OK → <span className="text-amber-400 font-semibold">Ajourné</span> (niveau insuffisant).</li>
            <li>Au moins <span className="text-red-400 font-bold">1 éliminatoire</span> cochée → <span className="text-red-400 font-semibold">Ajourné automatiquement</span>, quel que soit le score.</li>
          </ul>
        </div>

        {/* Conseils personnalisés (facultatif) */}
        {isTeacher && (
          <div className="rounded-xl border border-sky-500/25 bg-sky-950/10 p-5 mb-8" data-testid="conseils-block">
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-300 font-bold font-mono-spec mb-2">Conseils personnalisés à l&apos;élève (facultatif)</div>
              <textarea
                value={conseils}
                onChange={(e) => setConseils(e.target.value)}
                rows={3}
                data-testid="conseils-textarea"
                placeholder="Ex : revois les giratoires, contrôle systématiquement les rétros avant chaque changement de direction, respire avant de démarrer…"
                className="w-full rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13.5px] p-3 focus:outline-none focus:border-sky-500/50"
              />
            </label>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between flex-wrap gap-3 pt-6 border-t border-white/5">
          <Link to="/cours-interactifs" data-testid="back-to-tools" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition">
            <ArrowLeft size={14} /> Tous les outils
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={reset} data-testid="grille-reset" className={`inline-flex items-center gap-2 h-11 px-4 rounded-full border text-[13px] font-semibold transition ${resetConfirm ? 'bg-red-700 border-red-600 text-white shadow-md shadow-red-900/40' : 'border-white/15 bg-white/5 hover:bg-white/10 text-white'}`}>
              <RotateCcw size={14} /> {resetConfirm ? 'Confirmer la remise à zéro' : 'Réinitialiser'}
            </button>
            <button onClick={() => window.print()} data-testid="grille-print" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition">
              <Printer size={14} /> Imprimer / PDF
            </button>
            {isTeacher && (
              <button
                onClick={openShare}
                data-testid="grille-share-qr"
                disabled={evalCount === 0 || !meta.eleve.trim()}
                title={evalCount === 0 ? 'Évalue les critères' : (!meta.eleve.trim() ? "Renseigne le nom de l'élève" : 'Générer QR Code élève')}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 disabled:bg-red-900/40 disabled:cursor-not-allowed text-white text-[13px] font-semibold shadow-lg shadow-red-900/40 transition"
              >
                <QrCode size={14} /> Envoyer à l&apos;élève (QR)
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 text-[11.5px] text-white/40 font-mono-spec inline-flex items-center gap-2">
          <ShieldCheck size={11} /> Grille type permis B · 31 critères · codevoiture.fr
        </div>

        {/* Modal de partage QR Code */}
        {shareOpen && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 no-print" data-testid="share-modal" onClick={() => setShareOpen(false)}>
            <div className="bg-[#0d0d0e] border border-white/10 rounded-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setShareOpen(false)}
                data-testid="share-modal-close"
                className="absolute top-3 right-3 w-8 h-8 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center"
              >
                <X size={14} />
              </button>

              {shareStatus !== 'done' && (
                <>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4">
                    <Share2 size={12} className="text-red-400" />
                    <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Envoyer par QR Code</span>
                  </div>
                  <h3 className="font-serif-editorial text-white text-[22px] leading-tight">Générer la fiche pour {meta.eleve || "l'élève"}</h3>
                  <p className="mt-2 text-white/65 text-[13px] leading-relaxed">
                    L&apos;élève scannera un QR code et verra <strong className="text-white">le détail de son examen blanc</strong>, les critères ratés et les leçons REMC à retravailler — <strong className="text-emerald-400">sans créer de compte</strong>.
                  </p>
                  <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-3 text-[12.5px] text-white/70 space-y-1">
                    <div><span className="text-white/45">Élève :</span> <span className="text-white font-semibold">{meta.eleve || '—'}</span></div>
                    <div><span className="text-white/45">Date :</span> <span className="text-white">{meta.date || 'auto'}</span></div>
                    <div><span className="text-white/45">Score :</span> <span className="text-white font-semibold">{okPct}% ({stats.ok}/{total} OK)</span></div>
                    <div><span className="text-white/45">Verdict :</span> <span className="text-white font-semibold">{verdict}</span></div>
                  </div>
                  {shareError && (
                    <div className="mt-3 rounded-md border border-red-500/40 bg-red-950/30 px-3 py-2 text-red-300 text-[12.5px]" data-testid="share-error">{shareError}</div>
                  )}
                  <div className="mt-5 flex items-center justify-end gap-2">
                    <button onClick={() => setShareOpen(false)} className="h-10 px-4 rounded-full border border-white/15 text-white/80 hover:border-white/35 text-[12.5px] font-semibold">Annuler</button>
                    <button
                      onClick={saveAndShare}
                      disabled={shareStatus === 'saving'}
                      data-testid="share-save-btn"
                      className="h-10 px-5 rounded-full bg-red-600 hover:bg-red-500 disabled:bg-red-900/40 text-white text-[12.5px] font-bold inline-flex items-center gap-1.5"
                    >
                      {shareStatus === 'saving' ? (<><Loader2 size={13} className="animate-spin" /> Enregistrement…</>) : (<><QrCode size={13} /> Générer QR Code</>)}
                    </button>
                  </div>
                </>
              )}

              {shareStatus === 'done' && shareResult && (
                <>
                  <div className="text-center">
                    <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-3" />
                    <h3 className="font-serif-editorial text-white text-[22px]">Fiche prête ✓</h3>
                    <p className="mt-1.5 text-white/65 text-[13px]">Fais scanner ce QR code à <strong className="text-white">{meta.eleve}</strong>.</p>
                  </div>
                  <div className="mt-5 flex flex-col items-center gap-2">
                    <div className="bg-white p-4 rounded-2xl shadow-[0_0_40px_-8px_rgba(239,68,68,0.4)]" data-testid="exam-qr-code">
                      <QRCodeSVG value={shareResult.fullUrl} size={200} level="M" />
                    </div>
                    <div className="text-red-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5">
                      <QrCode size={11} /> Scan · résultats affichés instantanément
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 bg-[#0a0a0b] border border-white/15 rounded-xl px-3 py-2.5">
                    <span className="flex-1 text-left text-white text-[12px] font-mono-spec break-all">{shareResult.fullUrl}</span>
                    <button onClick={copyUrl} data-testid="exam-copy-url" className="shrink-0 h-8 px-2.5 rounded-full border border-white/15 text-white/85 hover:border-white/35 text-[11.5px] font-semibold inline-flex items-center gap-1">
                      {copied ? <><CheckCircle2 size={11} className="text-emerald-400" /> Copié</> : <><Copy size={11} /> Copier</>}
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <button onClick={openSMS} data-testid="exam-share-sms" className="h-10 px-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1"><MessageCircle size={12} /> SMS</button>
                    <button onClick={openWhatsApp} data-testid="exam-share-whatsapp" className="h-10 px-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1"><Send size={12} /> WhatsApp</button>
                    <button onClick={openEmail} data-testid="exam-share-email" className="h-10 px-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1"><Mail size={12} /> Email</button>
                  </div>
                  <div className="mt-4 text-center">
                    <Link to={shareResult.url} target="_blank" data-testid="exam-preview-link" className="text-white/60 hover:text-white text-[11.5px] font-mono-spec underline underline-offset-4">Aperçu élève →</Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
          </>
        )}
      </section>
    </PageShell>
  );
}
